import React, { useState } from "react";
import { toast } from "react-toastify";
import { FiKey, FiX } from "react-icons/fi";
import { usersClient, type AdminUser } from "./usersClient";

/** Fenêtre « Forcer un nouveau mot de passe » : action d'administrateur sur le compte d'un
 *  AUTRE utilisateur (le sien passe par « Mon compte », où le mot de passe actuel est vérifié).
 *  Le serveur applique les gardes : jamais sur soi-même, et un compte super-administrateur ne
 *  peut être ciblé que par un super-administrateur. Chaque tentative est auditée. */
export function ForcePasswordModal({ user, onClose, onDone }: { user: AdminUser; onClose: () => void; onDone?: () => void }): React.ReactElement {
	const [password, setPassword] = useState("");
	const [confirm, setConfirm] = useState("");
	const [saving, setSaving] = useState(false);

	const generate = () => {
		// Générateur local : 16 caractères mêlant les 4 classes exigées par la politique Auth0.
		const sets = ["ABCDEFGHJKLMNPQRSTUVWXYZ", "abcdefghijkmnopqrstuvwxyz", "23456789", "!@#$%*+-?"];
		const bytes = new Uint32Array(16);
		window.crypto.getRandomValues(bytes);
		const all = sets.join("");
		let value = sets.map((set, i) => set[bytes[i] % set.length]).join("");
		for (let i = sets.length; i < bytes.length; i++) value += all[bytes[i] % all.length];
		value = value
			.split("")
			.sort(() => 0.5 - Math.random())
			.join("");
		setPassword(value);
		setConfirm(value);
		void navigator.clipboard?.writeText(value).then(
			() => toast.info("Mot de passe généré et copié dans le presse-papiers."),
			() => toast.info("Mot de passe généré, copiez-le depuis le champ.")
		);
	};

	const submit = async () => {
		if (password.length < 8) {
			toast.error("Le mot de passe doit compter au moins 8 caractères.");
			return;
		}
		if (password !== confirm) {
			toast.error("Les deux saisies ne correspondent pas.");
			return;
		}
		if (!window.confirm(`Redéfinir le mot de passe de « ${user.email} » ? L'utilisateur devra utiliser ce nouveau mot de passe dès maintenant.`)) return;
		setSaving(true);
		try {
			await usersClient.forcePassword(user.id, password);
			toast.success(`Mot de passe redéfini pour ${user.email}.`);
			onDone?.();
			onClose();
		} catch (e: any) {
			toast.error(e?.response?.data?.message ?? "Impossible de redéfinir le mot de passe.");
		} finally {
			setSaving(false);
		}
	};

	return (
		<div className="fixed inset-0 z-[2100] flex items-center justify-center bg-slate-900/40 p-4" role="dialog" aria-modal="true" aria-label="Forcer un nouveau mot de passe">
			<div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
				<div className="flex items-start justify-between gap-3">
					<div>
						<h2 className="text-lg font-bold text-slate-900">Forcer un nouveau mot de passe</h2>
						<p className="mt-1 text-sm text-slate-500">
							Pour <span className="font-semibold text-slate-700">{user.email}</span>. Le changement est immédiat et journalisé ; communiquez le mot de passe par un canal sûr.
						</p>
					</div>
					<button type="button" onClick={onClose} className="rounded-lg p-2 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600" aria-label="Fermer">
						<FiX className="h-5 w-5" />
					</button>
				</div>

				<div className="mt-4 space-y-3">
					<label className="block">
						<span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Nouveau mot de passe</span>
						<input
							type="text"
							value={password}
							onChange={(e) => setPassword(e.target.value)}
							autoComplete="new-password"
							className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 font-mono text-sm text-slate-800 outline-none transition focus:border-sky-400"
							placeholder="Au moins 8 caractères"
						/>
					</label>
					<label className="block">
						<span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Confirmer</span>
						<input
							type="text"
							value={confirm}
							onChange={(e) => setConfirm(e.target.value)}
							autoComplete="new-password"
							className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 font-mono text-sm text-slate-800 outline-none transition focus:border-sky-400"
							placeholder="Répétez le mot de passe"
						/>
					</label>
					<button type="button" onClick={generate} className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 transition hover:bg-slate-50">
						<FiKey className="h-3.5 w-3.5" /> Générer un mot de passe fort
					</button>
				</div>

				<div className="mt-5 flex justify-end gap-2">
					<button type="button" onClick={onClose} disabled={saving} className="rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50">
						Annuler
					</button>
					<button
						type="button"
						onClick={submit}
						disabled={saving || password.length === 0}
						className="rounded-full bg-[#0095DB] px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-[#007cb8] disabled:cursor-not-allowed disabled:opacity-60"
					>
						{saving ? "Application…" : "Redéfinir le mot de passe"}
					</button>
				</div>
			</div>
		</div>
	);
}

export default ForcePasswordModal;
